import React, { useState } from 'react'
import Menu, { MenuProps } from 'components/menu'
import {
  Pencil,
  Arrow,
  Delete,
  Visible,
  Lock,
  Asterisk,
  Group,
  Colour,
  Copy,
  Zip,
  SizeWidthIncrease,
  SizeWidthDecrease,
  Variable,
  Tick,
  Unlock,
  AddFilter,
  Filter,
  Cross
} from 'components/icons'
import { createTableColumnViewAction, updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { useProject } from 'hooks/useProject'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useColumnTypePicker } from 'components/spreadsheet/components/column-type-picker/useColumnTypePicker'
import ColumnTypePickerMenu from 'components/spreadsheet/components/column-type-picker'
import { ITableViewColumn, ITableViewFilter, ITableViewGroup, ITableViewSort, SelectKindOptions } from 'types'
import { getDefaultFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'
import { IDownloadZipProgress, IFilterType, sortDirections } from 'components/spreadsheet/types'
import { useApplicationStore } from 'hooks/application'
import { exportAttachmentsAsZip } from 'components/spreadsheet/helpers/export'
import { getTreeLeafColumnId } from 'components/spreadsheet/helpers/grouping'
import { DEFAULT_FORM_COLUMN_WIDTH } from 'components/spreadsheet/constants/const'
import { MenuIconSpinner, MenuItem, MenuLabel, MenuList, MenuSeparator } from 'components/menu/items'
import { directionOptions, sortLabel } from 'components/spreadsheet/components/menu/views/sort/directions'
import { PANEL_WIDTH, panelAnchor } from 'components/menu/panel'

interface ColumnMenuProps extends MenuProps {
  setEditColumnMenu: (open: boolean, panel?: 'formula') => void
  setDeleteColumnModal: (open: boolean) => void
}

const ColumnMenu: React.FC<ColumnMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  setEditColumnMenu,
  setDeleteColumnModal
}) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData, setFilterMenu, setColourMenu, setGroupMenu, showToolbar } =
    useDataContext()
  const { displayErrorMessage } = useApplicationStore()
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false)
  const [zipProgress, setZipProgress] = useState<IDownloadZipProgress>({ done: 0, total: 0 })

  const capabilities = getViewCapabilities(spreadsheetData.viewDetails.type).columnMenu

  const column = menuState.columnId
    ? spreadsheetData.viewDetails.columns.find((column: ITableViewColumn) => column.publicId === menuState.columnId)
    : undefined

  const treeLeafColumnId = getTreeLeafColumnId(
    spreadsheetData.viewDetails.columns,
    spreadsheetData.userConfiguration.groupSettings,
    spreadsheetData.viewDetails.treeGroupView
  )

  const hideDuplicateColumn = !spreadsheetData.isAdmin || !column || column.isJoined || column.isSynced

  const picker = useColumnTypePicker(spreadsheetData, setSpreadsheetData)

  // Open the type picker to insert a column before the clicked one — matching the header + button:
  // picking a type adds it instantly, or the configure affordance opens a second stage first. Closes
  // this context menu first. Anchor to the clicked column's header (captured before the insert shifts
  // the grid): the new column takes that column's position, so this pins the picker below where the
  // new column appears — rather than at the context-menu's right-click location, which can be far off.
  const handleInsertColumn = (sortOrder: number): void => {
    const header = menuState.columnId ? document.getElementById(`column/${menuState.columnId}`) : null
    const rect = header?.getBoundingClientRect()
    const anchor = rect
      ? { top: `${rect.bottom}px`, left: `${rect.left}px` }
      : { top: menuState.top, left: menuState.left }
    setMenuState({ ...menuState, open: false })
    picker.openForNewColumn(sortOrder, anchor)
  }

  const handleDuplicateColumn = () => {
    if (!column) {
      displayErrorMessage("This column couldn't be found. Refresh the page and try again.")
      return
    }

    if (hideDuplicateColumn) {
      displayErrorMessage("Only table Owners can duplicate columns, and joined or synced columns can't be duplicated.")
      return
    }

    const { publicId, ...newColumn } = column as ITableViewColumn & {
      kindOptions?: SelectKindOptions
    }
    void publicId
    if (newColumn.kindOptions?.tableOptions) {
      newColumn.kindOptions = {
        ...newColumn.kindOptions,
        tableOptions: { ...newColumn.kindOptions.tableOptions, cachedOptions: undefined }
      }
    }

    // Delete all the properties that are not needed for a new column
    newColumn.isJoined = false
    newColumn.isSynced = false
    newColumn.name = `${newColumn.name} (Copy)`
    newColumn.sortOrder = newColumn.sortOrder + 1
    const payload = { ...newColumn, context: { projectId: project.publicId } }

    createTableColumnViewAction(
      spreadsheetData.viewDetails.publicId,
      payload,
      setSpreadsheetData,
      handleOnSuccess,
      handleOnFailure
    )
  }

  const handleOnSuccess = () => {
    setMenuState({ ...menuState, open: false })
  }

  const handleOnFailure = (error: any) => {
    displayErrorMessage(error)
  }

  // The toolbar collapses into a single dropdown on narrow viewports — which is what browser zoom
  // and Windows display magnification produce — so the filter/colour buttons these sub-menus were
  // anchored to may not be in the DOM at all. Fall back to the column menu's own anchor rather than
  // doing nothing, and let Menu clamp the result into the viewport
  // The filter and colour panels open under their toolbar buttons, as they do from the buttons themselves.
  const panelAnchorUnder = (toolbarButtonId: string, width: number = PANEL_WIDTH.lg) => {
    const toolbarButton = document.getElementById(toolbarButtonId)
    return toolbarButton ? panelAnchor(toolbarButton, width) : { top: menuState.top, left: menuState.left }
  }

  const openGroups = () => {
    setGroupMenu({
      open: true,
      ...panelAnchorUnder('group-toolbar-button', PANEL_WIDTH.md),
      right: 'auto',
      bottom: 'auto'
    })
    setMenuState({ ...menuState, open: false })
  }

  // Opens the Conditional formatting panel: with a new rule on this column to fill in, or as it is, to edit.
  const openColours = (withNewRule: boolean) => {
    if (!column) return
    setColourMenu({
      open: true,
      ...panelAnchorUnder('colour-toolbar-button'),
      right: 'auto',
      bottom: 'auto',
      ...(withNewRule && {
        defaultColour: {
          columnId: column.publicId,
          columnName: column.name,
          filterType: getDefaultFilterTypeForColType(column.kind),
          multipleValues: [],
          backgroundColour: '#FFFFFF',
          fontColour: '#333333',
          fontBold: false,
          fontItalic: false,
          fontUnderline: false,
          applyToEntireRow: false,
          value: ''
        }
      })
    })
    setMenuState({ ...menuState, open: false })
  }

  const removeColours = () => {
    setSpreadsheetData({
      type: 'UPDATE_COLOURS',
      colours: spreadsheetData.userConfiguration.colourSettings.filter(
        (setting) => setting.columnId !== menuState.columnId
      )
    })
    setMenuState({ ...menuState, open: false })
  }

  // Opens the Filter panel: with a new filter on this column to fill in, or as it is, to edit.
  const openFilters = (withNewFilter: boolean) => {
    if (!column) return
    setFilterMenu({
      open: true,
      ...panelAnchorUnder('filter-toolbar-button'),
      right: 'auto',
      bottom: 'auto',
      ...(withNewFilter && {
        defaultFilter: {
          columnId: column.publicId,
          columnName: column.name,
          filterType: getDefaultFilterTypeForColType(column.kind),
          value: '',
          multipleValues: [],
          orGroup: 'main'
        }
      })
    })
    setMenuState({ ...menuState, open: false })
  }

  const removeFilters = () => {
    setSpreadsheetData({
      type: 'UPDATE_FILTERS',
      filters: spreadsheetData.userConfiguration.filterSettings.filter(
        (setting: ITableViewFilter) => setting.columnId !== menuState.columnId
      )
    })
    setMenuState({ ...menuState, open: false })
  }

  const columnSorted =
    column &&
    spreadsheetData.userConfiguration.sortSettings.find(
      (sortSetting: ITableViewSort) => sortSetting.columnId === column.publicId
    )
  const filterCount = spreadsheetData.userConfiguration.filterSettings.filter(
    (setting: ITableViewFilter) => setting.columnId === menuState.columnId
  ).length
  const colourCount = spreadsheetData.userConfiguration.colourSettings.filter(
    (setting) => setting.columnId === menuState.columnId
  ).length
  // Something that's on for the column, as its rows under a blue heading.
  const active = (label: string, rows: React.ReactNode[]) => [
    <MenuLabel key={`label-${label}`} active>
      {label}
    </MenuLabel>,
    ...rows
  ]
  const isGrouped = spreadsheetData.userConfiguration.groupSettings.some(
    (setting: ITableViewGroup) => setting.columnId === menuState.columnId
  )
  const isLocked =
    !!column && spreadsheetData.userConfiguration.rowLockSettings.some((rule) => rule.columnId === column.publicId)
  const formWidth = column?.formWidth ?? DEFAULT_FORM_COLUMN_WIDTH

  const sortBy = (direction: sortDirections) => {
    if (column) {
      if (columnSorted) setSpreadsheetData({ type: 'DELETE_SORT', columnId: column.publicId })
      setSpreadsheetData({ type: 'ADD_SORT', columnId: column.publicId, columnName: column.name })
      if (direction === sortDirections.desc) {
        setSpreadsheetData({ type: 'CHANGE_SORT_DIRECTION', columnId: column.publicId, newDirection: direction })
      }
    }
    handleOnSuccess()
  }

  const toggleGroup = () => {
    if (column) {
      setSpreadsheetData(
        isGrouped
          ? { type: 'DELETE_GROUP', columnId: column.publicId }
          : { type: 'ADD_GROUP', columnId: column.publicId, columnName: column.name }
      )
      handleOnSuccess()
    }
  }

  const toggleLock = () => {
    if (column) {
      const existingLocks = spreadsheetData.userConfiguration.rowLockSettings
      setSpreadsheetData({
        type: 'UPDATE_LOCKS',
        locks: isLocked
          ? existingLocks.filter((rule) => rule.columnId !== column.publicId)
          : [
              ...existingLocks,
              {
                columnId: column.publicId,
                columnName: column.name,
                filterType: IFilterType.all_values,
                value: '',
                multipleValues: [],
                applyToEntireRow: false
              }
            ]
      })
    }
    handleOnSuccess()
  }

  const toggleRequired = () => {
    if (!column) return
    setSpreadsheetData({ type: column.required ? 'UNREQUIRE_COLUMN' : 'REQUIRE_COLUMN', columnId: column.publicId })
    handleOnSuccess()
  }

  const toggleFormWidth = () => {
    if (column) {
      updateColumn(
        project.publicId,
        'formWidth',
        spreadsheetData,
        setSpreadsheetData,
        column.publicId,
        formWidth === 2 ? 1 : 2,
        handleOnSuccess,
        handleOnFailure
      )
    }
  }

  const downloadAttachments = async () => {
    if (downloadingZip || !spreadsheetData || !column) return
    setDownloadingZip(true)
    await exportAttachmentsAsZip(spreadsheetData, setZipProgress, displayErrorMessage, column.publicId)
    setDownloadingZip(false)
  }

  const isAdmin = spreadsheetData.isAdmin
  const filterable = capabilities.filter && showToolbar
  const colourable = capabilities.colour && showToolbar
  const lockable = isAdmin && capabilities.lockColumn && (!column || !column.isSynced)

  // Sorting says what it does for the column's type, as the Sort panel does: A to Z, oldest first.
  const sortRows = () =>
    directionOptions(column?.kind).map((direction) => (
      <MenuItem
        key={`sort-${direction.value}`}
        icon={direction.icon}
        role="menuitemradio"
        aria-checked={columnSorted?.direction === direction.value}
        end={columnSorted?.direction === direction.value ? <Tick className="size-4 text-body" /> : undefined}
        onClick={() => sortBy(direction.value)}
      >
        {sortLabel(direction.label)}
      </MenuItem>
    ))
  const canHide =
    capabilities.hideColumn &&
    showToolbar &&
    (!column?.required || column?.isJoined) &&
    !isGrouped &&
    treeLeafColumnId !== column?.publicId

  // The menu in groups, a line between each that has anything in it: the column itself, what's on for it,
  // what could be, its settings, and deleting it.
  const sections: React.ReactNode[][] = [
    [
      capabilities.editColumn && isAdmin && (
        <MenuItem key="edit" icon={<Pencil />} onClick={() => setEditColumnMenu(true)} data-tour="edit-column-button">
          Edit column
        </MenuItem>
      ),
      capabilities.editColumn && isAdmin && column && !column.isJoined && !column.isSynced && (
        <MenuItem
          key="formula"
          icon={<Variable />}
          onClick={() => setEditColumnMenu(true, 'formula')}
          data-testid="column-menu-formula"
        >
          {column.formulaEnabled ? 'Edit formula' : 'Add formula'}
        </MenuItem>
      ),
      capabilities.insertColumn && isAdmin && (
        <MenuItem
          key="insert"
          icon={<Arrow type="left" />}
          onClick={() => {
            if (menuState.columnNumber !== undefined) handleInsertColumn(menuState.columnNumber)
          }}
        >
          Insert column before
        </MenuItem>
      ),
      capabilities.duplicateColumn && isAdmin && !hideDuplicateColumn && (
        <MenuItem
          key="duplicate"
          icon={<Copy />}
          onClick={() => {
            if (menuState.columnNumber !== undefined) handleDuplicateColumn()
          }}
        >
          Duplicate column
        </MenuItem>
      )
    ],
    // What's on for the column, each under its own blue heading, most important first, with ways to change
    // it and, under a plain ×, to remove it; what isn't is offered in the group below.
    capabilities.group && isGrouped
      ? active('Group', [
          // The Group panel opens from the view row, so only where it shows.
          showToolbar && (
            <MenuItem key="edit-groups" icon={<Group />} onClick={openGroups}>
              Edit groups
            </MenuItem>
          ),
          <MenuItem key="ungroup" icon={<Cross />} onClick={toggleGroup}>
            Remove group
          </MenuItem>
        ])
      : [],
    filterable && filterCount > 0
      ? active('Filter', [
          <MenuItem key="edit-filters" icon={<Filter />} onClick={() => openFilters(false)}>
            Edit filters
          </MenuItem>,
          <MenuItem key="remove-filters" icon={<Cross />} onClick={removeFilters}>
            Remove filters
          </MenuItem>
        ])
      : [],
    capabilities.sort && columnSorted
      ? active('Sort', [
          ...sortRows(),
          <MenuItem
            key="unsort"
            icon={<Cross />}
            onClick={() => {
              if (column) setSpreadsheetData({ type: 'DELETE_SORT', columnId: column.publicId })
              handleOnSuccess()
            }}
          >
            Remove sort
          </MenuItem>
        ])
      : [],
    colourable && colourCount > 0
      ? active('Conditional formatting', [
          <MenuItem key="edit-colours" icon={<Colour />} onClick={() => openColours(false)}>
            Edit rules
          </MenuItem>,
          <MenuItem key="remove-colours" icon={<Cross />} onClick={removeColours}>
            Remove rules
          </MenuItem>
        ])
      : [],
    lockable && isLocked
      ? active('Lock', [
          <MenuItem key="unlock" icon={<Unlock />} onClick={toggleLock}>
            Unlock column
          </MenuItem>
        ])
      : [],
    [
      filterable && filterCount === 0 && (
        <MenuItem key="add-filter" icon={<AddFilter />} onClick={() => openFilters(true)}>
          Add filter
        </MenuItem>
      ),
      ...(capabilities.sort && !columnSorted ? sortRows() : []),
      colourable && colourCount === 0 && (
        <MenuItem key="colour" icon={<Colour />} onClick={() => openColours(true)}>
          Add conditional formatting
        </MenuItem>
      ),
      capabilities.group && !isGrouped && (
        <MenuItem key="group" icon={<Group />} onClick={toggleGroup}>
          Group column
        </MenuItem>
      ),
      canHide && (
        <MenuItem
          key="hide"
          icon={<Visible />}
          onClick={() => {
            if (menuState.columnId !== undefined)
              setSpreadsheetData({ type: 'HIDE_COLUMN', columnId: menuState.columnId })
            handleOnSuccess()
          }}
        >
          Hide column
        </MenuItem>
      ),
      capabilities.downloadAttachments && column?.kind === 'attachment' && (
        <MenuItem key="download" icon={downloadingZip ? <MenuIconSpinner /> : <Zip />} onClick={downloadAttachments}>
          {downloadingZip ? `Downloading ${zipProgress.done} of ${zipProgress.total}` : 'Download attachments'}
        </MenuItem>
      )
    ],
    [
      lockable && !isLocked && (
        <MenuItem
          key="lock"
          icon={<Lock />}
          disabledReason={column?.formulaEnabled ? 'Formula columns are automatically locked' : undefined}
          onClick={toggleLock}
        >
          Lock column
        </MenuItem>
      ),
      isAdmin && capabilities.requiredField && column && !column.isJoined && (
        <MenuItem key="required" icon={<Asterisk />} onClick={toggleRequired}>
          {column.required ? 'Make optional' : 'Make required'}
        </MenuItem>
      ),
      isAdmin && capabilities.formWidth && column && (
        <MenuItem
          key="form-width"
          icon={formWidth === 2 ? <SizeWidthDecrease /> : <SizeWidthIncrease />}
          onClick={toggleFormWidth}
        >
          {formWidth === 2 ? 'Set form width to 1 column' : 'Set form width to 2 columns'}
        </MenuItem>
      )
    ],
    [
      isAdmin && capabilities.deleteColumn && (!column || !column.isSynced) && (
        <MenuItem key="delete" icon={<Delete />} variant="destructive" onClick={() => setDeleteColumnModal(true)}>
          Delete column
        </MenuItem>
      )
    ]
  ]
  const shown = sections.map((rows) => rows.filter(Boolean)).filter((rows) => rows.length > 0)

  return (
    <>
      <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
        <MenuList>
          {shown.map((rows, index) => (
            <React.Fragment key={index}>
              {index > 0 && <MenuSeparator />}
              {rows}
            </React.Fragment>
          ))}
        </MenuList>
      </Menu>
      <ColumnTypePickerMenu id="insert-column-type-menu" picker={picker} />
    </>
  )
}

export default ColumnMenu
